import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const metadata = transaction?.attributes?.metadata || {};
  const protectedData = transaction?.attributes?.protectedData || {};

  const secondInstallmentStatus =
    metadata?.secondInstallmentStatus === 'paid' ||
    metadata?.installments?.secondInstallmentStatus === 'paid' ||
    protectedData?.paymentInstallment === 'second';

  const thirdInstallmentStatus =
    metadata?.thirdInstallmentStatus === 'paid' ||
    metadata?.installments?.thirdInstallmentStatus === 'paid' ||
    protectedData?.paymentInstallment === 'third';

  const payinTotal = transaction?.attributes?.payinTotal;
  const isChildTx = protectedData?.isChildTransaction || protectedData?.childTransaction;

  const baseTotal = isProvider
    ? transaction?.attributes?.payoutTotal || payinTotal
    : payinTotal;

  let totalPrice = baseTotal;

  if (baseTotal && !isChildTx) {
    let extraAmount = 0;
    if (secondInstallmentStatus) {
      const secondAmt =
        metadata?.secondInstallmentAmount ||
        metadata?.installments?.secondInstallmentAmount ||
        Math.round((baseTotal.amount / 3) * 4);
      extraAmount += secondAmt;
    }
    if (thirdInstallmentStatus) {
      const thirdAmt =
        metadata?.thirdInstallmentAmount ||
        metadata?.installments?.thirdInstallmentAmount ||
        baseTotal.amount;
      extraAmount += thirdAmt;
    }
    if (extraAmount > 0) {
      totalPrice = new Money(baseTotal.amount + extraAmount, baseTotal.currency);
    }
  }
  const formattedTotalPrice = formatMoney(intl, totalPrice);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
